import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getEnrichedCartItems,
  getOrCreateCart,
  mergeLocalCartToSupabase,
  upsertCartItem,
  removeCartItemByProduct,
  updateCartItemQuantity as updateCartItemQuantityApi,
} from '../lib/maison-adji-cart-api';
import { useAuth } from './AuthContext';
import {
  MaisonAdjiCartContext,
  type MaisonAdjiCartItem,
} from './MaisonAdjiCartContext';

const STORAGE_KEY = 'maison_adji_cart';

function loadFromStorage(): MaisonAdjiCartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage(items: MaisonAdjiCartItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (_) {}
}

function clearStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}

export function MaisonAdjiCartProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState<MaisonAdjiCartItem[]>([]);
  const [profileId, setProfileId] = useState<string | null>(null);
  const mergedRef = useRef(false);

  useEffect(() => {
    const uid = user?.id ?? null;
    setProfileId(uid);

    if (!uid) {
      setItems(loadFromStorage());
      mergedRef.current = false;
      return;
    }

    if (authLoading) return;

    let cancelled = false;

    (async () => {
      if (!mergedRef.current) {
        mergedRef.current = true;
        const localItems = loadFromStorage();
        if (localItems.length > 0) {
          try {
            await mergeLocalCartToSupabase(
              uid,
              localItems.map((i) => ({
                productId: i.productId,
                quantity: i.quantity,
                priceXof: i.priceXof,
                priceEur: i.priceEur,
              }))
            );
          } catch (_) {}
          if (!cancelled) clearStorage();
        }
      }
      if (cancelled) return;
      try {
        const enriched = await getEnrichedCartItems(uid);
        if (!cancelled) setItems(enriched);
      } catch (_) {
        if (!cancelled) setItems(loadFromStorage());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]);

  useEffect(() => {
    if (profileId == null) saveToStorage(items);
  }, [profileId, items]);

  const addItem = useCallback(
    async (item: Omit<MaisonAdjiCartItem, 'quantity'> & { quantity?: number }) => {
      const qty = item.quantity ?? 1;
      const uid = user?.id ?? null;
      const newItem = { ...item, quantity: qty };

      const applyOptimistic = (prev: MaisonAdjiCartItem[]) => {
        const existing = prev.find((i) => i.productId === item.productId);
        if (existing) {
          return prev.map((i) =>
            i.productId === item.productId ? { ...i, quantity: i.quantity + qty } : i
          );
        }
        return [...prev, newItem];
      };

      if (uid) {
        setItems((prev) => applyOptimistic(prev));
        try {
          const cartId = await getOrCreateCart(uid);
          if (!cartId) return;
          const unitPrice = item.priceXof ?? item.priceEur ?? 0;
          const ok = await upsertCartItem(cartId, item.productId, qty, unitPrice, null);
          if (!ok) return;
          // Ne pas appeler getEnrichedCartItems ici : il peut renvoyer un panier vide
          // (ex. autre cart_id si getOrCreateCart est rappelé) et faire disparaître la ligne.
          // L’état optimiste reste ; le panier sera resynchronisé au prochain chargement (useEffect).
        } catch (_) {
          // keep optimistic state on error
        }
      } else {
        setItems((prev) => applyOptimistic(prev));
      }
    },
    [user?.id]
  );

  const removeItem = useCallback(
    async (productId: string) => {
      const uid = user?.id ?? null;
      if (uid) {
        const cartId = await getOrCreateCart(uid);
        if (cartId) {
          await removeCartItemByProduct(cartId, productId);
          const enriched = await getEnrichedCartItems(uid);
          setItems(enriched);
        }
      } else {
        setItems((prev) => prev.filter((i) => i.productId !== productId));
      }
    },
    [user?.id]
  );

  const updateQuantity = useCallback(
    async (productId: string, quantity: number) => {
      if (quantity < 1) {
        removeItem(productId);
        return;
      }
      const uid = user?.id ?? null;
      const applyOptimistic = (prev: MaisonAdjiCartItem[]) =>
        prev.map((i) => (i.productId === productId ? { ...i, quantity } : i));

      if (uid) {
        setItems(applyOptimistic);
        try {
          const cartId = await getOrCreateCart(uid);
          if (cartId) {
            await updateCartItemQuantityApi(cartId, productId, quantity);
          }
          // Ne pas rappeler setItems ici : l’état optimiste est déjà bon, évite le flicker du prix
        } catch (_) {
          try {
            const enriched = await getEnrichedCartItems(uid);
            setItems(enriched);
          } catch (_) {}
        }
      } else {
        setItems(applyOptimistic);
      }
    },
    [user?.id, removeItem]
  );

  const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);

  const value = {
    items,
    totalQuantity,
    addItem,
    removeItem,
    updateQuantity,
  };

  return (
    <MaisonAdjiCartContext.Provider value={value}>
      {children}
    </MaisonAdjiCartContext.Provider>
  );
}
